<template>
  <div class="xl:w-full mt-1 mb-3">
    <NBox class="w-full" elevation="none">
      <div class="h-8 mr-auto w-fit pe-2">
        <ChartInfo
          v-if="isSuccessSymbolCandleStick"
          :selected-timespan="selectedTimespan"
          :first-point-of-chart="firstPointOfChart ?? {}"
          :last-point-of-chart="lastPointOfChart ?? {}"
          :selected-point="selectedPoint"
          @percentage-changed="handlePercentageChanged"
        />
      </div>

      <div
        v-if="isFetchingSymbolCandleStick || loadingChart"
        class="w-full h-[176px] animate-pulse flex justify-center items-center bg-no-repeat bg-cover bg-[url('@/assets/images/sample_chart.svg')]"
      />

      <div
        v-else-if="isErrorSymbolCandleStick"
        class="w-full h-[176px] flex flex-col items-center bg-no-repeat bg-cover bg-[url('@/assets/images/sample_chart.svg')]"
      >
        <NegarErrorLightIcon size="48" class="text-gray mb-2" />

        <NText
          class="text-neutral-primary-hover pb-2"
          variant="caption1"
          weight="bold"
        >
          {{ t('symbol.charts.error') }}
        </NText>

        <NButton
          class="inline-block rounded-full"
          variant="outline"
          size="mini"
          color="gray"
          @click="handleSelectedTimespan(selectedTimespan)"
        >
          <NText class="text-gray" weight="bold" variant="overline">
            {{ t('misc.retry') }}
          </NText>
        </NButton>
      </div>

      <LineChart
        v-if="!isFetchingSymbolCandleStick && !isErrorSymbolCandleStick"
        :selected-timespan="selectedTimespan"
        :chart-data="symbolCandleStickLineChartData || []"
        :reference-price="symbol.referencePrice ?? 0"
        :series-color="seriesColor"
        :chart-percentage="chartPercentage"
        :symbol="symbol"
        @point-click="handlePointInfoChanged"
      />

      <div
        v-if="
          !symbolCandleStickLineChartData?.length &&
          !isFetchingSymbolCandleStick &&
          !isErrorSymbolCandleStick &&
          !loadingChart
        "
        class="w-full h-[176px] mt-4 flex justify-center items-center bg-no-repeat bg-cover bg bg-[url('@/assets/images/sample_chart.svg')]"
      >
        <NText
          class="text-neutral-primary-hover"
          variant="caption1"
          weight="bold"
        >
          {{ t('symbol.charts.noData') }}
        </NText>
      </div>

      <div class="relative flex mt-2 mx-3">
        <div
          class="overflow-x-scroll hide-scroll-bar w-full flex items-center justify-center"
        >
          <ChartFilters
            :selected-timespan="selectedTimespan"
            @change-selected-timespan="handleSelectedTimespan"
          />
        </div>
      </div>
    </NBox>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { NegarErrorLightIcon } from '@negar/icons';
import { sleep } from '@libs/utils';
import { useGetSymbolCandleStick } from '@core/symbol';

import {
  LineChart,
  ChartInfo,
  SYMBOL_LINE_CHART_FILTERS,
  useLiveSymbolQuery,
  useActiveSymbolIsin,
  useSymbolChart,
  ChartFilters,
} from '@/modules/symbol';

const selectedChartFilters = computed(
  () => SYMBOL_LINE_CHART_FILTERS[selectedTimespan.value]
);
const lineChartData = computed(
  () => symbolCandleStickLineChartData.value || []
);

const isFetchingSymbolCandleStick = computed(
  () =>
    isLoadingSymbolCandleStick.value || isRevalidatingSymbolCandleStick.value
);

const { t } = useI18n();

const { activeIsin } = useActiveSymbolIsin();
const { symbol } = useLiveSymbolQuery(activeIsin);

const loadingChart = ref<boolean>(true);

const {
  firstPointOfChart,
  lastPointOfChart,
  seriesColor,
  selectedPoint,
  chartPercentage,
  selectedTimespan,

  handlePercentageChanged,
  handlePointInfoChanged,
} = useSymbolChart(lineChartData);

const {
  symbolCandleStickLineChartData,
  isLoadingSymbolCandleStick,
  isErrorSymbolCandleStick,
  isRevalidatingSymbolCandleStick,
  isSuccessSymbolCandleStick,
  getSymbolCandleStick,
} = useGetSymbolCandleStick({
  isin: activeIsin,
  selectedTimespan,
  selectedChartFilters,
  tradeState: computed<number>(() => symbol.value.instrumentTradingState),
});

const handleSelectedTimespan = async (
  timeSpan: string,
  isFirstRender = false
) => {
  selectedTimespan.value = timeSpan;

  await sleep(10);

  getSymbolCandleStick().then(({ data }) => {
    const isTodaySelected = timeSpan === SYMBOL_LINE_CHART_FILTERS.oneDay.id;
    const isDataEmpty = data?.data.candleStickDetails?.length === 0;
    const changeTimespan = isTodaySelected && isDataEmpty && isFirstRender;

    loadingChart.value = changeTimespan;

    if (changeTimespan) {
      handleSelectedTimespan(SYMBOL_LINE_CHART_FILTERS.oneWeek.id);
    }
  });
};

watch(
  () => symbol.value.isin,
  () => {
    handleSelectedTimespan(selectedTimespan.value, true);
  },
  { immediate: true }
);
</script>
